// Pure helpers for grouping social accounts by platform, extracted from
// SocialAccountsModule.tsx so the Connected tab can render one card per
// account instead of collapsing multiple accounts on the same platform
// (e.g. several Facebook Pages) into a single card via `.find()`.
// See specs/completed/social-accounts-multi-page-fix.md Phase 1.

export interface SocialAccountData {
    account_id: string;
    platform: string;
    username: string | null;
    display_name: string | null;
    profile_url: string | null;
    phone_number: string | null;
    credential_id: string | null;
    has_oauth: boolean;
    branding_applied: boolean;
    branding_applied_at: string | null;
    branding_status: string | null;
    branding_last_error: string | null;
    branding_last_draft_id: string | null;
    connected_at: string;
    status: string;
    ramp_ends_at: string | null;
    ramp_days_remaining: number | null;
    page_id: string | null;
}

export type PlatformKey = 'instagram' | 'facebook' | 'linkedin' | 'twitter';

/**
 * Groups active accounts by platform, preserving input order (the backend
 * returns accounts ordered by connected_at desc). Platforms not present in
 * `platformKeys` are ignored — no unknown-platform accounts exist in
 * production today, but this keeps the grid from throwing on an unknown key.
 */
export function groupAccountsByPlatform(
    accounts: SocialAccountData[],
    platformKeys: PlatformKey[],
): Record<PlatformKey, SocialAccountData[]> {
    const result = {} as Record<PlatformKey, SocialAccountData[]>;
    for (const key of platformKeys) {
        result[key] = [];
    }
    for (const account of accounts) {
        if (account.status !== 'active') continue;
        const key = account.platform as PlatformKey;
        if (result[key]) {
            result[key].push(account);
        }
    }
    return result;
}

/**
 * Per-card identity label. Instagram/Twitter show a handle (@username);
 * Facebook/LinkedIn show a display name (no @, since Page names contain
 * spaces). Falls back to a short account_id when neither is available.
 */
export function accountIdentityLabel(account: SocialAccountData): string {
    if (account.platform === 'instagram' || account.platform === 'twitter') {
        if (account.username) return `@${account.username}`;
        return account.account_id.slice(0, 8);
    }
    if (account.display_name) return account.display_name;
    if (account.username) return account.username;
    return account.account_id.slice(0, 8);
}
